import { useRef, useCallback, useMemo } from 'react';
import type { Stroke, Point, MapNote } from '@roll1/shared';
import { LIMITS } from '@roll1/shared';
import { GridSpatialIndex } from '../utils/spatialIndex';
import type { CanvasEngine } from './useCanvasEngine';
import type { UseDrawingReturn } from './useDrawing';

export type DrawingSelectionMode = 'idle' | 'selecting' | 'moving';

export interface DrawingSelectionState {
  selectedStrokeIds: Set<string>;
  selectedNoteIds: Set<string>;
  selectionBounds: { x: number; y: number; width: number; height: number } | null;
  mode: DrawingSelectionMode;
  dragStart: Point | null;
  /** Current marquee rectangle in world coordinates */
  marqueeRect: { x: number; y: number; width: number; height: number } | null;
  /** Accumulated move offset during drag */
  moveOffset: { dx: number; dy: number };
}

export interface UseDrawingSelectionReturn {
  state: React.MutableRefObject<DrawingSelectionState>;
  spatialIndex: React.MutableRefObject<GridSpatialIndex>;
  /** Attempt to select a stroke at world coordinates. Returns true if a stroke was hit. */
  selectAt: (wx: number, wy: number) => boolean;
  /** Clear current selection */
  clearSelection: () => void;
  /** Start marquee selection from a world point */
  startMarquee: (wx: number, wy: number) => void;
  /** Update marquee rectangle during drag */
  updateMarquee: (wx: number, wy: number) => void;
  /** Finalize marquee selection */
  endMarquee: () => void;
  /** Start moving selected strokes */
  startMove: (wx: number, wy: number) => void;
  /** Update move offset during drag */
  updateMove: (wx: number, wy: number) => void;
  /** Finalize move, returns {dx, dy} delta or null if no move happened */
  endMove: () => { dx: number; dy: number; strokeIds: string[]; noteIds: string[] } | null;
  /** Apply a move delta to strokes locally (for optimistic updates and undo) */
  applyMoveLocal: (strokeIds: string[], dx: number, dy: number) => void;
  /** Delete strokes locally by IDs, returns deleted strokes for undo */
  deleteLocal: (strokeIds: string[]) => Stroke[];
  /** Restore strokes locally (for undo of delete) */
  restoreLocal: (strokes: Stroke[]) => void;
  /** Get currently selected stroke IDs as array */
  getSelectedIds: () => string[];
  /** Get currently selected note IDs as array */
  getSelectedNoteIds: () => string[];
  /** Check if point is inside current selection bounding box */
  isInSelection: (wx: number, wy: number) => boolean;
  /** Rebuild spatial index from current strokes */
  rebuildIndex: () => void;
  /** Remove specific stroke IDs from selection (e.g. when deleted by another user) */
  deselectIds: (ids: string[]) => void;
}

export function useDrawingSelection(
  engine: CanvasEngine,
  drawing: UseDrawingReturn,
  getNotesRef?: () => MapNote[],
): UseDrawingSelectionReturn {
  const HIT_RADIUS = 20;

  const state = useRef<DrawingSelectionState>({
    selectedStrokeIds: new Set(),
    selectedNoteIds: new Set(),
    selectionBounds: null,
    mode: 'idle',
    dragStart: null,
    marqueeRect: null,
    moveOffset: { dx: 0, dy: 0 },
  });

  const spatialIndex = useRef<GridSpatialIndex>(new GridSpatialIndex(engine.gridSize || 70));

  const recalcBounds = useCallback(() => {
    const s = state.current;
    if (s.selectedStrokeIds.size === 0 && s.selectedNoteIds.size === 0) {
      s.selectionBounds = null;
      return;
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const stroke of drawing.strokes.current) {
      if (!s.selectedStrokeIds.has(stroke.id)) continue;
      if (stroke.type === 'path' && stroke.points) {
        for (const p of stroke.points) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
      } else {
        const x1 = stroke.x1 ?? 0;
        const y1 = stroke.y1 ?? 0;
        const x2 = stroke.x2 ?? 0;
        const y2 = stroke.y2 ?? 0;
        minX = Math.min(minX, x1, x2);
        minY = Math.min(minY, y1, y2);
        maxX = Math.max(maxX, x1, x2);
        maxY = Math.max(maxY, y1, y2);
      }
    }
    // Include selected note positions in bounding box
    if (getNotesRef && s.selectedNoteIds.size > 0) {
      for (const note of getNotesRef()) {
        if (!s.selectedNoteIds.has(note.id)) continue;
        if (note.x < minX) minX = note.x;
        if (note.y < minY) minY = note.y;
        if (note.x > maxX) maxX = note.x;
        if (note.y > maxY) maxY = note.y;
      }
    }
    if (minX === Infinity) {
      s.selectionBounds = null;
    } else {
      s.selectionBounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
  }, [drawing, getNotesRef]);

  const selectAt = useCallback((wx: number, wy: number): boolean => {
    const s = state.current;
    const hitId = spatialIndex.current.hitTest(drawing.strokes.current, wx, wy, HIT_RADIUS);
    s.selectedStrokeIds.clear();
    s.selectedNoteIds.clear();
    if (hitId) {
      s.selectedStrokeIds.add(hitId);
    } else if (getNotesRef) {
      // Check notes for hit: if click within HIT_RADIUS of note center, select it
      for (const note of getNotesRef()) {
        const dx = wx - note.x;
        const dy = wy - note.y;
        if (dx * dx + dy * dy <= HIT_RADIUS * HIT_RADIUS) {
          s.selectedNoteIds.add(note.id);
          break;
        }
      }
    }
    s.mode = 'idle';
    s.marqueeRect = null;
    recalcBounds();
    engine.markDirty('ui');
    return hitId !== null || s.selectedNoteIds.size > 0;
  }, [drawing, engine, recalcBounds, getNotesRef]);

  const clearSelection = useCallback(() => {
    const s = state.current;
    s.selectedStrokeIds.clear();
    s.selectedNoteIds.clear();
    s.selectionBounds = null;
    s.mode = 'idle';
    s.dragStart = null;
    s.marqueeRect = null;
    s.moveOffset = { dx: 0, dy: 0 };
    engine.markDirty('ui');
  }, [engine]);

  const startMarquee = useCallback((wx: number, wy: number) => {
    const s = state.current;
    s.mode = 'selecting';
    s.dragStart = { x: wx, y: wy };
    s.marqueeRect = { x: wx, y: wy, width: 0, height: 0 };
    s.selectedStrokeIds.clear();
    s.selectedNoteIds.clear();
    s.selectionBounds = null;
    engine.markDirty('ui');
  }, [engine]);

  const updateMarquee = useCallback((wx: number, wy: number) => {
    const s = state.current;
    if (s.mode !== 'selecting' || !s.dragStart) return;
    const x = Math.min(s.dragStart.x, wx);
    const y = Math.min(s.dragStart.y, wy);
    const width = Math.abs(wx - s.dragStart.x);
    const height = Math.abs(wy - s.dragStart.y);
    s.marqueeRect = { x, y, width, height };
    engine.markDirty('ui');
  }, [engine]);

  const endMarquee = useCallback(() => {
    const s = state.current;
    if (s.mode !== 'selecting' || !s.marqueeRect) {
      s.mode = 'idle';
      return;
    }

    const rect = s.marqueeRect;
    // Query spatial index for candidates
    const candidateIds = spatialIndex.current.query(rect);

    // Filter candidates by actual bounding box intersection
    s.selectedStrokeIds.clear();
    s.selectedNoteIds.clear();
    let count = 0;
    for (const stroke of drawing.strokes.current) {
      if (!candidateIds.includes(stroke.id)) continue;
      if (count >= LIMITS.MAX_SELECTED_STROKES) break;
      // Check bounding box intersection
      if (strokeIntersectsRect(stroke, rect)) {
        s.selectedStrokeIds.add(stroke.id);
        count++;
      }
    }

    // Select notes within marquee rect
    if (getNotesRef) {
      const rMaxX = rect.x + rect.width;
      const rMaxY = rect.y + rect.height;
      for (const note of getNotesRef()) {
        if (note.x >= rect.x && note.x <= rMaxX && note.y >= rect.y && note.y <= rMaxY) {
          s.selectedNoteIds.add(note.id);
        }
      }
    }

    s.mode = 'idle';
    s.marqueeRect = null;
    s.dragStart = null;
    recalcBounds();
    engine.markDirty('ui');
  }, [drawing, engine, recalcBounds, getNotesRef]);

  const startMove = useCallback((wx: number, wy: number) => {
    const s = state.current;
    s.mode = 'moving';
    s.dragStart = { x: wx, y: wy };
    s.moveOffset = { dx: 0, dy: 0 };
  }, []);

  const updateMove = useCallback((wx: number, wy: number) => {
    const s = state.current;
    if (s.mode !== 'moving' || !s.dragStart) return;
    const dx = wx - s.dragStart.x;
    const dy = wy - s.dragStart.y;
    s.moveOffset = { dx, dy };
    engine.markDirty('ui');
    engine.markDirty('drawing');
  }, [engine]);

  const endMove = useCallback((): { dx: number; dy: number; strokeIds: string[]; noteIds: string[] } | null => {
    const s = state.current;
    if (s.mode !== 'moving') return null;
    const { dx, dy } = s.moveOffset;
    const strokeIds = [...s.selectedStrokeIds];
    const noteIds = [...s.selectedNoteIds];
    s.mode = 'idle';
    s.dragStart = null;
    s.moveOffset = { dx: 0, dy: 0 };

    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return null;

    // Apply move locally for strokes
    applyMoveLocal(strokeIds, dx, dy);
    // Apply move locally for notes
    if (getNotesRef && noteIds.length > 0) {
      const noteIdSet = new Set(noteIds);
      for (const note of getNotesRef()) {
        if (noteIdSet.has(note.id)) {
          note.x += dx;
          note.y += dy;
        }
      }
    }

    return { dx, dy, strokeIds, noteIds };
  }, [drawing, engine, getNotesRef]);

  const applyMoveLocal = useCallback((strokeIds: string[], dx: number, dy: number) => {
    const idSet = new Set(strokeIds);
    for (const stroke of drawing.strokes.current) {
      if (!idSet.has(stroke.id)) continue;
      if (stroke.type === 'path' && stroke.points) {
        for (const p of stroke.points) { p.x += dx; p.y += dy; }
      }
      if (stroke.x1 !== undefined) stroke.x1 += dx;
      if (stroke.y1 !== undefined) stroke.y1 += dy;
      if (stroke.x2 !== undefined) stroke.x2 += dx;
      if (stroke.y2 !== undefined) stroke.y2 += dy;
      spatialIndex.current.update(stroke);
    }
    recalcBounds();
    drawing.invalidateCache();
    engine.markDirty('drawing');
    engine.markDirty('ui');
  }, [drawing, engine, recalcBounds]);

  const deleteLocal = useCallback((strokeIds: string[]): Stroke[] => {
    const idSet = new Set(strokeIds);
    const deleted: Stroke[] = [];
    const remaining: Stroke[] = [];
    for (const stroke of drawing.strokes.current) {
      if (idSet.has(stroke.id)) {
        deleted.push({ ...stroke, points: stroke.points ? stroke.points.map(p => ({ ...p })) : undefined });
        spatialIndex.current.remove(stroke.id);
      } else {
        remaining.push(stroke);
      }
    }
    drawing.strokes.current = remaining;
    drawing.invalidateCache();
    engine.markDirty('drawing');
    engine.markDirty('ui');
    return deleted;
  }, [drawing, engine]);

  const restoreLocal = useCallback((strokes: Stroke[]) => {
    for (const stroke of strokes) {
      drawing.strokes.current.push(stroke);
      spatialIndex.current.insert(stroke);
    }
    drawing.invalidateCache();
    engine.markDirty('drawing');
    engine.markDirty('ui');
  }, [drawing, engine]);

  const getSelectedIds = useCallback((): string[] => {
    return [...state.current.selectedStrokeIds];
  }, []);

  const getSelectedNoteIds = useCallback((): string[] => {
    return [...state.current.selectedNoteIds];
  }, []);

  const isInSelection = useCallback((wx: number, wy: number): boolean => {
    const bounds = state.current.selectionBounds;
    if (!bounds) return false;
    const pad = 4;
    return (
      wx >= bounds.x - pad &&
      wx <= bounds.x + bounds.width + pad &&
      wy >= bounds.y - pad &&
      wy <= bounds.y + bounds.height + pad
    );
  }, []);

  const rebuildIndex = useCallback(() => {
    spatialIndex.current.rebuild(drawing.strokes.current);
  }, [drawing]);

  const deselectIds = useCallback((ids: string[]) => {
    const s = state.current;
    let changed = false;
    for (const id of ids) {
      if (s.selectedStrokeIds.delete(id)) changed = true;
    }
    if (changed) {
      recalcBounds();
      engine.markDirty('ui');
    }
  }, [engine, recalcBounds]);

  return useMemo(() => ({
    state,
    spatialIndex,
    selectAt,
    clearSelection,
    startMarquee,
    updateMarquee,
    endMarquee,
    startMove,
    updateMove,
    endMove,
    applyMoveLocal,
    deleteLocal,
    restoreLocal,
    getSelectedIds,
    getSelectedNoteIds,
    isInSelection,
    rebuildIndex,
    deselectIds,
  }), [
    state, spatialIndex, selectAt, clearSelection,
    startMarquee, updateMarquee, endMarquee,
    startMove, updateMove, endMove,
    applyMoveLocal, deleteLocal, restoreLocal,
    getSelectedIds, getSelectedNoteIds, isInSelection,
    rebuildIndex, deselectIds,
  ]);
}

/** Check if a stroke's bounding box intersects with a rectangle */
function strokeIntersectsRect(
  stroke: Stroke,
  rect: { x: number; y: number; width: number; height: number },
): boolean {
  let sMinX: number, sMinY: number, sMaxX: number, sMaxY: number;
  if (stroke.type === 'path' && stroke.points && stroke.points.length > 0) {
    sMinX = Infinity; sMinY = Infinity; sMaxX = -Infinity; sMaxY = -Infinity;
    for (const p of stroke.points) {
      if (p.x < sMinX) sMinX = p.x;
      if (p.y < sMinY) sMinY = p.y;
      if (p.x > sMaxX) sMaxX = p.x;
      if (p.y > sMaxY) sMaxY = p.y;
    }
  } else {
    sMinX = Math.min(stroke.x1 ?? 0, stroke.x2 ?? 0);
    sMinY = Math.min(stroke.y1 ?? 0, stroke.y2 ?? 0);
    sMaxX = Math.max(stroke.x1 ?? 0, stroke.x2 ?? 0);
    sMaxY = Math.max(stroke.y1 ?? 0, stroke.y2 ?? 0);
  }

  const rMaxX = rect.x + rect.width;
  const rMaxY = rect.y + rect.height;

  return !(sMaxX < rect.x || sMinX > rMaxX || sMaxY < rect.y || sMinY > rMaxY);
}
